import React, { useState } from 'react';
import serialize from 'form-serialize';

import { useAppContext } from '@prisjakt/ui-app-context';
import { H3Text, H4Text } from '@prisjakt/ui/text/variants';
import MultiRadio, {
  Variants,
} from 'src/components/review-form/components/multi-radio';
import { Column } from '@prisjakt/ui/list/variants';
import { ThankYouScreen } from './thank-you-screen';

import { pushEvent } from 'src/client/data-layer';
import { useLocalStorage } from 'src/hooks/use-localstorage';

import { Question, SubmitButton, Modal } from './styles';

import { BaseProps } from './types';

type Props = Omit<BaseProps, 'description'> & {
  closeModal: () => void;
  isOpen: boolean;
  formId: string;
  onSubmit: () => void;
};

const OPTIONS = [0, 1, 2, 3, 4, 5];

// Component used for collecting UX feedback in the form of UUP
// Utility, Usability, Presentation
// https://uxdesign.cc/measuring-ux-account-for-aesthetics-600fa66f4cd9
// Responses are logged to Amplitude via GTM

export const FormModal = (props: Props) => {
  const {
    closeModal,
    isOpen,
    formId,
    onSubmit,
    storageId,
    title,
    utilityTitle,
    usabilityTitle,
    presentationTitle,
  } = props;

  const { t } = useAppContext();
  const [submitted, setSubmitted] = useState(false);
  const [_, setLocalStorage] = useLocalStorage(storageId, 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const result = serialize(e.target, { hash: true });
    // Formula for calculating UUP score (https://uxdesign.cc/measuring-ux-account-for-aesthetics-600fa66f4cd9)
    const score =
      (parseInt(result.utility, 10) * 3 +
        parseInt(result.usability, 10) * 2 +
        parseInt(result.presentation, 10)) /
      6;

    // Push the score to GTM and Amplitude where it will be stored
    pushEvent(`${formId}Submitted`, { score, ...result });
    setLocalStorage(new Date().valueOf());
    setSubmitted(true);
    onSubmit();
  };

  const labels = [
    t('feedback', 'uup.answer-label-1'),
    '',
    '',
    '',
    '',
    t('feedback', 'uup.answer-label-6'),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldShowCloseIcon
      width={526}
    >
      {submitted ? (
        <ThankYouScreen closeModal={closeModal} />
      ) : (
        <form onSubmit={handleSubmit}>
          <Column gap={1.5}>
            <H3Text>{title}</H3Text>

            <Column gap={2}>
              <Question>
                <H4Text>{utilityTitle}</H4Text>
                <MultiRadio
                  name="utility"
                  variant={Variants.scale}
                  options={OPTIONS}
                  labels={labels}
                  required
                />
              </Question>

              <Question>
                <H4Text>{usabilityTitle}</H4Text>
                <MultiRadio
                  name="usability"
                  variant={Variants.scale}
                  options={OPTIONS}
                  labels={labels}
                  required
                />
              </Question>

              <Question>
                <H4Text>{presentationTitle}</H4Text>
                <MultiRadio
                  name="presentation"
                  variant={Variants.scale}
                  options={OPTIONS}
                  labels={labels}
                  required
                />
              </Question>
            </Column>

            <SubmitButton htmlType="submit">
              {t('feedback', 'uup.submit-button')}
            </SubmitButton>
          </Column>
        </form>
      )}
    </Modal>
  );
};
